import { apiClient, getActivePvzId } from './_shared.js'

// ============================================
// PIN SECURITY (Block 15)
// ============================================

async function hashString(str) {
  const normalized = str.toLowerCase().trim()
  // crypto.subtle доступен только в secure context (HTTPS/localhost)
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoder = new TextEncoder()
    const data = encoder.encode(normalized)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }
  // Fallback: простой хеш для тестирования на HTTP (LAN)
  let hash = 0
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return 'fb_' + Math.abs(hash).toString(16).padStart(8, '0')
}

export async function setPin(userId, pin) {
  const pinHash = await hashString(pin)
  await apiClient('/auth/pin/set', {
    method: 'POST',
    body: JSON.stringify({ pin_hash: pinHash })
  })
}

export async function verifyPin(userId, pin) {
  const pinHash = await hashString(pin)
  return await apiClient('/auth/pin/verify', {
    method: 'POST',
    body: JSON.stringify({ userId, pin_hash: pinHash })
  })
}

export async function removePin(userId) {
  await apiClient('/auth/pin/remove', { method: 'POST' })
}

export async function setSecretQuestion(userId, question, answer) {
  const answerHash = await hashString(answer)
  await apiClient('/auth/secret/set', {
    method: 'POST',
    body: JSON.stringify({ question, answer_hash: answerHash })
  })
}

export async function verifySecretAnswer(userId, answer) {
  const answerHash = await hashString(answer)
  const res = await apiClient('/auth/secret/verify', {
    method: 'POST',
    body: JSON.stringify({ userId, answer_hash: answerHash })
  })
  return res.success
}

export async function resetPinBySecret(userId, answer, newPin) {
  const isCorrect = await verifySecretAnswer(userId, answer)
  if (!isCorrect) throw new Error('Неверный ответ на секретный вопрос')
  await setPin(userId, newPin)
}

export async function getPvzUsersForSwitch(orgId) {
  try {
    const data = await apiClient(`/employees?orgId=${orgId}`)
    return (data || []).map(u => ({
      id: u.id, full_name: u.full_name, avatar_url: u.avatar_url,
      role: u.role, hasPin: u.hasPin, login: u.login
    }))
  } catch(e) {
    return []
  }
}

// ============================================
// CONVERT CUSTOM OPERATIONS
// ============================================

/**
 * Подсчитать количество записей work_logs с кастомным типом.
 * Возвращает { count, minDate, maxDate }
 */
export async function countCustomOperations(customRuleId) {
  try {
    const pvzId = getActivePvzId()
    if (!pvzId) return { count: 0, minDate: null, maxDate: null }
    const data = await apiClient(`/work-logs/custom-count/${customRuleId}?pvzId=${pvzId}`)
    return data || { count: 0, minDate: null, maxDate: null }
  } catch(e) {
    return { count: 0, minDate: null, maxDate: null }
  }
}

/**
 * Конвертировать все записи кастомного типа в стандартный тип.
 * @param {string} customRuleId — ID кастомного правила
 * @param {string} targetType — 'acceptance' | 'issued' | 'returns'
 * @returns {{ converted: number }}
 */
export async function convertCustomOperations(customRuleId, targetType) {
  const pvzId = getActivePvzId()
  if (!pvzId) throw new Error('ПВЗ не выбран')
  
  const data = await apiClient(`/work-logs/custom-convert/${customRuleId}`, {
    method: 'PUT',
    body: JSON.stringify({ pvz_id: pvzId, targetType })
  })
  return data
}
